import { useState, useRef, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthProvider';
import { PenTool, Type, Link as LinkIcon, Image as ImageIcon, UploadCloud, Trash2, Plus, ChevronRight, ChevronLeft, Film, Target, Sparkles, Wand2, Check, DollarSign, Eye, Calendar, MapPin, Users, Globe, Megaphone } from 'lucide-react';
import AdvancedTargetingModal from './AdvancedTargetingModal';
import toast from 'react-hot-toast';

interface Creative {
  id: string;
  media_url: string;
  media_type: 'image' | 'video';
  primary_text: string;
  headline: string;
}

interface CampaignCreatorProps {
  onCreated: () => void;
}

export default function CampaignCreator({ onCreated }: CampaignCreatorProps) {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [activeCreativeIdx, setActiveCreativeIdx] = useState(0);
  const [wizardStep, setWizardStep] = useState(0); // 0=AI+Settings, 1=Creatives, 2=Targeting, 3=Summary

  const [campaign, setCampaign] = useState({
    name: '',
    destination_url: '',
    destination_type: 'website', // 'website' | 'facebook_form'
    daily_budget: 20,
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
    objective: 'OUTCOME_TRAFFIC',
    call_to_action: 'LEARN_MORE',
    meta_page_id: '',
  });

  const [socialAccounts, setSocialAccounts] = useState<any[]>([]);

  const [targeting, setTargeting] = useState<any>({
    age_min: 18,
    age_max: 65,
    genders: [0],
    geo_locations: { countries: ['PL'] },
  });
  const [showTargetingModal, setShowTargetingModal] = useState(false);

  const [leadForm, setLeadForm] = useState({
    name: 'Formularz Kontaktowy',
    privacy_policy_url: '',
    questions: ['FULL_NAME', 'EMAIL']
  });

  
  const [apiKeys, setApiKeys] = useState({ anthropic_key: '', image_api_key: '' });
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  useEffect(() => {
    if (user) {
      supabase.from('ad_booster_settings').select('anthropic_key, image_api_key').eq('user_id', user.id).maybeSingle()
        .then(({ data }) => {
          if (data) setApiKeys({ anthropic_key: data.anthropic_key || '', image_api_key: data.image_api_key || '' });
        });

      supabase.from('social_accounts')
        .select('*')
        .eq('owner_user_id', user.id)
        .eq('platform', 'facebook')
        .then(({ data }) => {
          if (data) {
            setSocialAccounts(data);
            if (data.length > 0) {
              setCampaign(prev => ({ ...prev, meta_page_id: prev.meta_page_id || data[0].page_id }));
            }
          }
        });
    }
  }, [user]);

  const generateAIText = async () => {
    if (!aiPrompt) return toast.error('Wpisz o czym ma być kampania');
    if (!apiKeys.anthropic_key && !apiKeys.image_api_key) return toast.error('Brak klucza API — przejdź do Ustawienia Ads → AutoWorker i dodaj klucz Gemini');
    
    setIsGeneratingAI(true);
    const toastId = toast.loading('🤖 AI projektuje kampanię...');
    try {
      // Call server-side Edge Function (no CORS, no browser rate limits)
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Zaloguj się ponownie');

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ads-ai-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ prompt: aiPrompt, type: 'campaign' }),
      });

      let result: any;
      try { result = await res.json(); } catch { throw new Error('Serwer nie odpowiedział prawidłowo'); }

      if (!res.ok) throw new Error(result.error || `Błąd serwera (${res.status})`);
      if (!result.success || !result.data) throw new Error('AI nie zwróciło danych kampanii');

      const resultJson = result.data;

      // ── Apply AI results to form ──
      const validObjectives = ['OUTCOME_TRAFFIC', 'OUTCOME_LEADS', 'OUTCOME_AWARENESS', 'OUTCOME_ENGAGEMENT', 'OUTCOME_SALES'];
      const validCTAs = ['LEARN_MORE', 'SHOP_NOW', 'SIGN_UP', 'CONTACT_US', 'DOWNLOAD', 'APPLY_NOW'];

      const endDate = new Date();
      endDate.setDate(endDate.getDate() + (resultJson.duration_days || 7));

      setCampaign(prev => ({
        ...prev,
        name: resultJson.name || prev.name,
        objective: validObjectives.includes(resultJson.objective) ? resultJson.objective : prev.objective,
        call_to_action: validCTAs.includes(resultJson.call_to_action) ? resultJson.call_to_action : prev.call_to_action,
        daily_budget: resultJson.daily_budget && resultJson.daily_budget >= 5 ? resultJson.daily_budget : prev.daily_budget,
        end_date: endDate.toISOString().split('T')[0],
      }));

      updateCreative(activeCreativeIdx, {
        primary_text: resultJson.primary_text || '',
        headline: resultJson.headline || '',
      });

      // Update targeting if AI suggested age range
      if (resultJson.age_min || resultJson.age_max) {
        setTargeting((prev: any) => ({
          ...prev,
          age_min: resultJson.age_min || prev.age_min,
          age_max: resultJson.age_max || prev.age_max,
        }));
      }

      // Store image prompt for later image generation
      if (resultJson.image_prompt) {
        (window as any).__lastAiImagePrompt = resultJson.image_prompt;
      }

      toast.success('✅ Kampania AI wygenerowana! Sprawdź wszystkie pola.', { id: toastId, duration: 4000 });
    } catch (e: any) {
      console.error('[AI Campaign]', e);
      toast.error('Błąd generowania AI: ' + (e.message || 'Nieznany błąd'), { id: toastId });
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const generateAIImage = async () => {
    const creativeText = cur.primary_text?.trim();
    const headlineText = cur.headline?.trim();
    const campaignName = campaign.name?.trim();
    const hasCreativeContent = creativeText || headlineText;

    if (!hasCreativeContent && !aiPrompt) {
      return toast.error('Uzupełnij tekst reklamy lub nagłówek, albo wpisz prompt AI');
    }
    
    setIsGeneratingImage(true);
    const toastId = toast.loading('🎨 AI generuje profesjonalne zdjęcie reklamowe...');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Zaloguj się ponownie');

      // ── STEP 1: Build the best possible English visual prompt ──
      let visualPrompt = (window as any).__lastAiImagePrompt || '';

      if (!visualPrompt) {
        // Ask AI to create an optimal image prompt from ad content
        if (hasCreativeContent) {
          try {
            toast.loading('🧠 AI tworzy prompt na podstawie reklamy...', { id: toastId });
            const translatePrompt = `Based on this ad content, create a HIGHLY DETAILED English image prompt (max 150 chars) for a stunning, premium marketing photo. Focus ONLY on the visual scene, lighting, and composition. NO text. Ad headline: "${headlineText || ''}" Ad text: "${creativeText || ''}" Campaign: "${campaignName || ''}". Return ONLY the image prompt, nothing else.`;
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ads-ai-generate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
              body: JSON.stringify({ prompt: translatePrompt, type: 'image_prompt' }),
            });
            const data = await res.json();
            if (data.success && data.data?.image_prompt) {
              visualPrompt = data.data.image_prompt;
            } else if (data.error) {
              console.warn('[AI Image] Prompt generation returned error:', data.error);
            }
          } catch (e) {
            console.warn('[AI Image] Prompt translation failed', e);
          }
        }

        // Fallback: build from context
        if (!visualPrompt) {
          if (aiPrompt) {
            visualPrompt = aiPrompt;
          } else {
            const objectiveMap: Record<string, string> = {
              'OUTCOME_TRAFFIC': 'modern business professional at desk with laptop, bright office, natural light, premium aesthetic, 8k resolution, photorealistic',
              'OUTCOME_LEADS': 'friendly business consultation meeting, two professionals shaking hands, warm sunlit office, cinematic lighting, highly detailed',
              'OUTCOME_AWARENESS': 'stunning premium brand showcase, elegant minimal design, golden hour lighting, masterpiece photography',
              'OUTCOME_ENGAGEMENT': 'vibrant lifestyle scene, happy diverse people, bright natural colors, outdoor setting, shot on 35mm lens',
              'OUTCOME_SALES': 'premium product photography, elegant marble surface, soft studio lighting, luxury aesthetic, highly detailed',
            };
            visualPrompt = objectiveMap[campaign.objective] || 'professional premium marketing photography, modern business concept, 8k resolution, photorealistic';
          }
        }
      }

      // Enhance the prompt for maximum quality
      const finalPrompt = `${visualPrompt}, professional advertising campaign photo, stunning commercial photography, ultra high quality, 16:9 landscape, no text overlay, no watermark, photorealistic, masterpiece`;

      // ── STEP 2: Generate via server-side Edge Function ──
      toast.loading('🎨 Generowanie zdjęcia...', { id: toastId });
      
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ads-ai-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ prompt: finalPrompt }),
      });

      let result: any;
      try { result = await res.json(); } catch { throw new Error('Serwer nie odpowiedział prawidłowo'); }

      if (!res.ok || !result.success) {
        throw new Error(result.error || `Błąd generowania zdjęcia (${res.status})`);
      }

      if (!result.image_url) {
        throw new Error('AI nie zwróciło zdjęcia');
      }

      updateCreative(activeCreativeIdx, { media_url: result.image_url, media_type: 'image' });
      toast.success('✅ Profesjonalne zdjęcie reklamowe wygenerowane!', { id: toastId, duration: 4000 });
    } catch (e: any) {
      console.error('[AI Image]', e);
      toast.error(`Błąd generowania zdjęcia: ${e.message || 'Spróbuj ponownie'}`, { id: toastId, duration: 5000 });
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const [creatives, setCreatives] = useState<Creative[]>([
    { id: Math.random().toString(36).substring(2), media_url: '', media_type: 'image', primary_text: '', headline: '' },
  ]);

  const updateCreative = (idx: number, patch: Partial<Creative>) => {
    setCreatives(prev => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
  };

  const addCreative = () => {
    if (creatives.length >= 6) return toast.error('Maksymalnie 6 kreacji');
    setCreatives(prev => [...prev, { id: Math.random().toString(36).substring(2), media_url: '', media_type: 'image', primary_text: '', headline: '' }]);
    setActiveCreativeIdx(creatives.length);
  };

  const removeCreative = (idx: number) => {
    if (creatives.length <= 1) return;
    setCreatives(prev => prev.filter((_, i) => i !== idx));
    if (activeCreativeIdx >= creatives.length - 1) setActiveCreativeIdx(Math.max(0, creatives.length - 2));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0] || !user) return;
    const file = e.target.files[0];
    if (file.size > 30 * 1024 * 1024) return toast.error('Max 30MB');
    try {
      setIsUploading(true);
      const ext = file.name.split('.').pop();
      const path = `${user.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from('media').upload(path, file);
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(path);
      const isVideo = file.type.startsWith('video/');
      updateCreative(activeCreativeIdx, { media_url: publicUrl, media_type: isVideo ? 'video' : 'image' });
      toast.success(isVideo ? 'Wideo wgrane!' : 'Grafika wgrana!');
    } catch (err) { console.error(err); toast.error('Błąd wgrywania'); }
    finally { setIsUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const validate = (): string | null => {
    if (!campaign.meta_page_id) return 'Wybierz Stronę (Fanpage), z której ma zostać opublikowana reklama.';
    if (!campaign.name) return 'Podaj nazwę kampanii';
    if (campaign.objective !== 'OUTCOME_LEADS' || campaign.destination_type === 'website') {
      if (!campaign.destination_url) return 'Podaj link docelowy';
    } else {
      if (!leadForm.privacy_policy_url) return 'Podaj link do polityki prywatności dla formularza';
      if (leadForm.questions.length === 0) return 'Wybierz co najmniej jedno pole formularza';
    }
    if (campaign.daily_budget < 5) return 'Minimalny budżet to 5 PLN';
    for (let i = 0; i < creatives.length; i++) {
      if (!creatives[i].media_url) return `Kreacja #${i + 1}: dodaj grafikę/wideo`;
      if (!creatives[i].primary_text) return `Kreacja #${i + 1}: wpisz tekst reklamy`;
    }
    return null;
  };

  const publishCampaign = async () => {
    const err = validate();
    if (err) return toast.error(err);
    try {
      setPublishing(true);
      toast.loading('Tworzenie kampanii...', { id: 'pub' });

      const finalTargeting = { ...targeting };

      const { data: camp, error: campErr } = await supabase.from('ad_campaigns').insert({
        user_id: user?.id, name: campaign.name, objective: campaign.objective,
        daily_budget: campaign.daily_budget, destination_url: campaign.destination_url,
        start_date: new Date(campaign.start_date).toISOString(),
        end_date: new Date(campaign.end_date).toISOString(),
        targeting: finalTargeting, status: 'draft', source_type: 'custom', call_to_action: campaign.call_to_action,
        lead_form_data: campaign.objective === 'OUTCOME_LEADS' && campaign.destination_type === 'facebook_form' ? leadForm : null,
        meta_page_id: campaign.meta_page_id,
      }).select('id').single();

      if (campErr || !camp) throw new Error(campErr?.message || 'Insert failed');

      const creativesPayload = creatives.map((c, i) => ({
        campaign_id: camp.id, media_url: c.media_url, media_type: c.media_type,
        primary_text: c.primary_text, headline: c.headline || campaign.name, sort_order: i,
      }));

      const { error: crErr } = await supabase.from('ad_campaign_creatives').insert(creativesPayload);
      if (crErr) throw new Error(crErr.message);

      toast.loading('Publikacja w Meta Ads...', { id: 'pub' });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Brak sesji — zaloguj się ponownie');

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ads-campaign-publisher`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ campaign_id: camp.id }),
      });

      let result: any;
      try {
        result = await res.json();
      } catch {
        throw new Error(`Serwer zwrócił nieprawidłową odpowiedź (HTTP ${res.status}). Sprawdź status kampanii w zakładce Aktywne Kampanie.`);
      }

      if (!res.ok) {
        const errorMsg = result.error || 'Publikacja nie powiodła się';
        const logSummary = result.log?.filter((l: any) => l.step.includes('error')).map((l: any) => l.msg).join('; ');
        throw new Error(logSummary ? `${errorMsg}\n\nSzczegóły: ${logSummary}` : errorMsg);
      }

      if (result.success) {
        const creativeCount = result.log?.filter((l: any) => l.step === 'creative' && l.msg.includes('created')).length || creatives.length;
        toast.success(`✅ Kampania AKTYWNA! ${creativeCount} kreacji opublikowanych w Meta Ads.`, { id: 'pub', duration: 5000 });
      } else {
        toast.success(`Kampania utworzona (status: ${result.status}). Sprawdź szczegóły w Aktywne Kampanie.`, { id: 'pub', duration: 5000 });
      }
      setCampaign({ ...campaign, name: '' });
      setCreatives([{ id: Math.random().toString(36).substring(2), media_url: '', media_type: 'image', primary_text: '', headline: '' }]);
      onCreated();
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Błąd publikacji', { id: 'pub' });
    } finally { setPublishing(false); }
  };

  const cur = creatives[activeCreativeIdx];
  const durationDays = Math.max(1, Math.ceil((new Date(campaign.end_date).getTime() - new Date(campaign.start_date).getTime()) / 86400000));
  const totalBudget = campaign.daily_budget * durationDays;

  const inputCls = "w-full px-4 py-3 bg-[#1e293b]/50 border border-slate-700 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all";
  const labelCls = "flex items-center gap-2 text-sm font-bold text-slate-300 mb-2";

  const wizardSteps = [
    { label: 'Kampania', icon: Megaphone, desc: 'Cel, budżet i harmonogram' },
    { label: 'Kreacje', icon: ImageIcon, desc: 'Grafiki, wideo i teksty' },
    { label: 'Targetowanie', icon: Target, desc: 'Grupa docelowa' },
    { label: 'Publikacja', icon: Check, desc: 'Podsumowanie i start' },
  ];

  const validateStep = (step: number): string | null => {
    if (step === 0) {
      if (!campaign.meta_page_id) return 'Wybierz Stronę (Fanpage)';
      if (!campaign.name) return 'Podaj nazwę kampanii';
      if (campaign.daily_budget < 5) return 'Minimalny budżet to 5 PLN';
      if (campaign.objective !== 'OUTCOME_LEADS' || campaign.destination_type === 'website') {
        if (!campaign.destination_url) return 'Podaj link docelowy';
      } else {
        if (!leadForm.privacy_policy_url) return 'Podaj link do polityki prywatności';
        if (leadForm.questions.length === 0) return 'Wybierz co najmniej jedno pole formularza';
      }
      return null;
    }
    if (step === 1) {
      for (let i = 0; i < creatives.length; i++) {
        if (!creatives[i].media_url) return `Kreacja #${i + 1}: dodaj grafikę/wideo`;
        if (!creatives[i].primary_text) return `Kreacja #${i + 1}: wpisz tekst reklamy`;
      }
      return null;
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(wizardStep);
    if (err) return toast.error(err);
    setWizardStep(s => Math.min(3, s + 1));
  };
  const goBack = () => setWizardStep(s => Math.max(0, s - 1));

  const objectiveLabels: Record<string, string> = { OUTCOME_TRAFFIC: 'Ruch', OUTCOME_LEADS: 'Leady', OUTCOME_AWARENESS: 'Zasięg', OUTCOME_ENGAGEMENT: 'Zaangażowanie', OUTCOME_SALES: 'Sprzedaż' };
  const ctaLabels: Record<string, string> = { LEARN_MORE: 'Dowiedz się więcej', SHOP_NOW: 'Kup teraz', SIGN_UP: 'Zapisz się', CONTACT_US: 'Skontaktuj się', DOWNLOAD: 'Pobierz', APPLY_NOW: 'Aplikuj teraz' };

  return (
    <div className="bg-[#0f172a] border border-slate-800 rounded-3xl overflow-hidden shadow-xl animate-in fade-in">
      {/* Wizard Stepper Header */}
      <div className="p-6 md:p-8 border-b border-slate-800/80">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 bg-blue-500/10 rounded-xl"><PenTool className="w-6 h-6 text-blue-400" /></div>
          <div>
            <h2 className="text-2xl font-bold text-white">Kreator Kampanii Reklamowej</h2>
            <p className="text-slate-500 text-sm">Meta Ads API v21.0 · Pipeline: Campaign → AdSet → Creative → Ad</p>
          </div>
        </div>
        {/* Stepper */}
        <div className="flex items-center gap-1 md:gap-2">
          {wizardSteps.map((step, i) => {
            const StepIcon = step.icon;
            const isCompleted = i < wizardStep;
            const isCurrent = i === wizardStep;
            return (
              <div key={i} className="flex items-center flex-1">
                <button
                  onClick={() => { if (i < wizardStep) setWizardStep(i); }}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl transition-all w-full ${
                    isCurrent ? 'bg-indigo-600/20 border border-indigo-500/50 text-white' :
                    isCompleted ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 cursor-pointer hover:bg-emerald-500/20' :
                    'bg-slate-800/50 border border-slate-800 text-slate-500'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-black shrink-0 ${
                    isCurrent ? 'bg-indigo-600 text-white' :
                    isCompleted ? 'bg-emerald-500/20 text-emerald-400' :
                    'bg-slate-800 text-slate-600'
                  }`}>
                    {isCompleted ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                  </div>
                  <div className="hidden md:block text-left min-w-0">
                    <p className="text-xs font-bold truncate">{step.label}</p>
                    <p className="text-[10px] opacity-60 truncate">{step.desc}</p>
                  </div>
                </button>
                {i < wizardSteps.length - 1 && (
                  <div className={`w-4 md:w-8 h-0.5 shrink-0 mx-1 rounded-full transition-colors ${
                    isCompleted ? 'bg-emerald-500/40' : 'bg-slate-800'
                  }`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="p-6 md:p-8 space-y-8">

        {/* ══════════════ STEP 0: Campaign Settings ══════════════ */}
        {wizardStep === 0 && (<div className="space-y-8 animate-in fade-in slide-in-from-right-4">
        
        {/* AI Generator Section */}
        <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 p-6 rounded-2xl border border-indigo-500/30">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-400" /> Asystent AI Kampanii
          </h3>
          <div className="flex flex-col md:flex-row gap-4">
            <textarea 
              className={inputCls + " resize-none flex-1"} 
              rows={2} 
              placeholder="Opisz swoimi słowami o czym ma być kampania (np. 'Zrób mi kampanię na letnią wyprzedaż sukienek w moim butiku, cel to sprzedaż')"
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
            />
            <button 
              onClick={generateAIText}
              disabled={isGeneratingAI || (!apiKeys.anthropic_key && !apiKeys.image_api_key)}
              className={`px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all whitespace-nowrap min-w-[200px] ${
                (!apiKeys.anthropic_key && !apiKeys.image_api_key) ? 'bg-slate-700/50 text-slate-500 cursor-not-allowed' : 
                isGeneratingAI ? 'bg-indigo-600/50 text-indigo-200' : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/25'
              }`}
            >
              {isGeneratingAI ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Wand2 className="w-5 h-5" />}
              {(!apiKeys.anthropic_key && !apiKeys.image_api_key) ? 'Brak klucza API' : 'Wygeneruj Kampanię AI'}
            </button>
          </div>
        </div>

        {/* Campaign Settings (same step) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="lg:col-span-2">
            <label className={labelCls}>Strona Facebook (Fanpage)</label>
            <select 
              value={campaign.meta_page_id} 
              onChange={e => setCampaign({ ...campaign, meta_page_id: e.target.value })} 
              className={inputCls}
            >
              <option value="" disabled>Wybierz stronę (wymagane)</option>
              {socialAccounts.map(acc => (
                <option key={acc.id} value={acc.page_id}>{acc.page_name}</option>
              ))}
            </select>
            {socialAccounts.length === 0 && (
              <p className="text-xs text-rose-400 mt-2 font-bold">Brak podłączonych stron. Przejdź do 'Ustawienia Ads', aby podłączyć Facebooka.</p>
            )}
          </div>
          <div>
            <label className={labelCls}><PenTool className="w-4 h-4 text-indigo-400" /> Nazwa kampanii</label>
            <input type="text" placeholder="np. Letnia Promocja 2026" value={campaign.name} onChange={e => setCampaign({ ...campaign, name: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Cel kampanii</label>
            <select value={campaign.objective} onChange={e => setCampaign({ ...campaign, objective: e.target.value })} className={inputCls}>
              <option value="OUTCOME_TRAFFIC">Ruch (Traffic)</option>
              <option value="OUTCOME_LEADS">Leady (Pozyskiwanie kontaktów)</option>
              <option value="OUTCOME_AWARENESS">Zasięg (Awareness)</option>
              <option value="OUTCOME_ENGAGEMENT">Zaangażowanie</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Przycisk akcji (CTA)</label>
            <select value={campaign.call_to_action} onChange={e => setCampaign({ ...campaign, call_to_action: e.target.value })} className={inputCls}>
              <option value="LEARN_MORE">Dowiedz się więcej</option>
              <option value="SHOP_NOW">Kup teraz</option>
              <option value="SIGN_UP">Zapisz się</option>
              <option value="CONTACT_US">Skontaktuj się</option>
              <option value="DOWNLOAD">Pobierz</option>
              <option value="APPLY_NOW">Aplikuj teraz</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Budżet dzienny (PLN)</label>
            <input type="number" min={5} max={5000} value={campaign.daily_budget} onChange={e => setCampaign({ ...campaign, daily_budget: Number(e.target.value) })} className={inputCls} />
          </div>
        </div>

        {campaign.objective === 'OUTCOME_LEADS' && (
          <div className="bg-[#1e293b]/50 p-6 rounded-2xl border border-indigo-500/30">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <Type className="w-5 h-5 text-indigo-400" /> Konfiguracja pozyskiwania leadów
            </h3>
            <div className="flex gap-4 mb-6">
              <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
                <input type="radio" name="dest_type" checked={campaign.destination_type === 'website'} onChange={() => setCampaign({ ...campaign, destination_type: 'website' })} className="accent-indigo-500 w-4 h-4" />
                Ruch na stronę WWW (Formularz na stronie)
              </label>
              <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
                <input type="radio" name="dest_type" checked={campaign.destination_type === 'facebook_form'} onChange={() => setCampaign({ ...campaign, destination_type: 'facebook_form' })} className="accent-indigo-500 w-4 h-4" />
                Formularz błyskawiczny Facebook (Instant Form)
              </label>
            </div>

            {campaign.destination_type === 'facebook_form' && (
              <div className="space-y-4 animate-in fade-in">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Nazwa formularza w Meta</label>
                    <input type="text" value={leadForm.name} onChange={e => setLeadForm({ ...leadForm, name: e.target.value })} className={inputCls} placeholder="np. Kontakt - Promocja" />
                  </div>
                  <div>
                    <label className={labelCls}>Link do polityki prywatności (Wymagane)</label>
                    <input type="url" value={leadForm.privacy_policy_url} onChange={e => setLeadForm({ ...leadForm, privacy_policy_url: e.target.value })} className={inputCls} placeholder="https://twojastrona.pl/polityka" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Wymagane pola (zaznacz)</label>
                  <div className="flex gap-4">
                    {['FULL_NAME', 'EMAIL', 'PHONE'].map(q => (
                      <label key={q} className="flex items-center gap-2 text-slate-300">
                        <input type="checkbox" checked={leadForm.questions.includes(q)}
                          onChange={e => {
                            if (e.target.checked) setLeadForm({ ...leadForm, questions: [...leadForm.questions, q] });
                            else setLeadForm({ ...leadForm, questions: leadForm.questions.filter(x => x !== q) });
                          }}
                          className="accent-indigo-500 w-4 h-4 rounded" />
                        {q === 'FULL_NAME' ? 'Imię i nazwisko' : q === 'EMAIL' ? 'Adres Email' : 'Numer Telefonu'}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {(campaign.objective !== 'OUTCOME_LEADS' || campaign.destination_type === 'website') && (
          <div>
            <label className={labelCls}><LinkIcon className="w-4 h-4 text-indigo-400" /> Link docelowy (URL kampanii)</label>
            <input type="url" placeholder="https://twojastrona.pl/oferta" value={campaign.destination_url} onChange={e => setCampaign({ ...campaign, destination_url: e.target.value })} className={inputCls + " font-mono text-sm"} />
          </div>
        )}

        {/* Budget + Dates + Targeting */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className={labelCls}>Budżet dzienny (PLN)</label>
            <input type="number" min={5} max={5000} value={campaign.daily_budget} onChange={e => setCampaign({ ...campaign, daily_budget: Number(e.target.value) })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Data startu</label>
            <input type="date" value={campaign.start_date} onChange={e => setCampaign({ ...campaign, start_date: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Data zakończenia</label>
            <input type="date" value={campaign.end_date} onChange={e => setCampaign({ ...campaign, end_date: e.target.value })} className={inputCls} />
          </div>
        </div>
        </div>)}

        {/* ══════════════ STEP 1: Creatives ══════════════ */}
        {wizardStep === 1 && (<div className="space-y-6 animate-in fade-in slide-in-from-right-4">

        {/* Creatives Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">Kreacje reklamowe ({creatives.length}/6)</h3>
            <button onClick={addCreative} className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-bold transition-all">
              <Plus className="w-4 h-4" /> Dodaj kreację
            </button>
          </div>

          {/* Creative Tabs */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
            {creatives.map((c, i) => (
              <button key={c.id} onClick={() => setActiveCreativeIdx(i)}
                className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${i === activeCreativeIdx ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>
                {c.media_type === 'video' ? <Film className="w-3.5 h-3.5" /> : <ImageIcon className="w-3.5 h-3.5" />}
                Kreacja #{i + 1}
                {creatives.length > 1 && (
                  <span onClick={(e) => { e.stopPropagation(); removeCreative(i); }} className="ml-1 w-5 h-5 rounded-full bg-rose-500/30 hover:bg-rose-500 flex items-center justify-center text-[10px]">✕</span>
                )}
              </button>
            ))}
          </div>

          {/* Active Creative Editor */}
          {cur && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 bg-[#0b0f19] rounded-2xl border border-slate-800">
              <div className="space-y-4">
                <div>
                  <label className={labelCls}><Type className="w-4 h-4 text-indigo-400" /> Tekst główny reklamy (primary_text)</label>
                  <textarea rows={4} placeholder="Napisz angażujący tekst reklamowy..." value={cur.primary_text}
                    onChange={e => updateCreative(activeCreativeIdx, { primary_text: e.target.value })} className={inputCls + " resize-none"} />
                </div>
                <div>
                  <label className={labelCls}><Type className="w-4 h-4 text-indigo-400" /> Nagłówek (headline)</label>
                  <input type="text" placeholder="Krótki, chwytliwy nagłówek" value={cur.headline}
                    onChange={e => updateCreative(activeCreativeIdx, { headline: e.target.value })} className={inputCls} />
                </div>
              </div>

              <div>
                <label className={labelCls}><ImageIcon className="w-4 h-4 text-indigo-400" /> Media (zdjęcie lub wideo)</label>
                {!cur.media_url ? (
                  <>
                    <div className="border-2 border-dashed border-slate-700 hover:border-indigo-500/50 rounded-2xl p-8 flex flex-col items-center justify-center bg-[#1e293b]/30 cursor-pointer transition-all group"
                      onClick={() => fileInputRef.current?.click()}>
                      <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*,video/*" className="hidden" />
                      {isUploading ? (
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-10 h-10 border-4 border-slate-700 border-t-indigo-500 rounded-full animate-spin" />
                          <p className="text-slate-400">Wgrywanie...</p>
                        </div>
                      ) : (
                        <>
                          <UploadCloud className="w-10 h-10 text-indigo-400 mb-3" />
                          <p className="text-slate-300 font-bold">Kliknij, aby wgrać</p>
                          <p className="text-slate-500 text-xs mt-1">Zdjęcie (1080x1080) lub Wideo. Max 30MB.</p>
                        </>
                      )}
                    </div>
                    <button 
                      onClick={generateAIImage}
                      disabled={isGeneratingImage}
                      className={`mt-3 w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${
                        isGeneratingImage ? 'bg-purple-600/50 text-purple-200 cursor-wait' : 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-500/20'
                      }`}
                    >
                      {isGeneratingImage ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {isGeneratingImage ? 'Generowanie...' : '🎨 Wygeneruj zdjęcie AI'}
                    </button>
                  </>

                ) : (
                  <div className="relative rounded-2xl overflow-hidden border border-slate-700 group h-52 bg-slate-900 flex items-center justify-center">
                    {cur.media_type === 'video' ? (
                      <video src={cur.media_url} className="max-h-full max-w-full" controls />
                    ) : (
                      <img src={cur.media_url} className="max-h-full max-w-full object-contain" />
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button onClick={() => updateCreative(activeCreativeIdx, { media_url: '' })}
                        className="px-4 py-2 bg-rose-500/20 text-rose-400 border border-rose-500/50 rounded-xl hover:bg-rose-500 hover:text-white transition-colors flex items-center gap-2 font-bold">
                        <Trash2 className="w-4 h-4" /> Zmień
                      </button>
                    </div>
                  </div>
                )}
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 h-px bg-slate-800" /><span className="text-xs text-slate-500 uppercase font-bold">lub link</span><div className="flex-1 h-px bg-slate-800" />
                </div>
                <input type="url" placeholder="https://link-do-media.jpg" value={cur.media_url}
                  onChange={e => updateCreative(activeCreativeIdx, { media_url: e.target.value })} className={inputCls + " mt-2 font-mono text-sm"} />
              </div>
            </div>
          )}
        </div>
        </div>)}

        {/* ══════════════ STEP 2: Targeting ══════════════ */}
        {wizardStep === 2 && (<div className="space-y-6 animate-in fade-in slide-in-from-right-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between p-6 bg-[#1e293b]/30 rounded-2xl border border-slate-800">
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-1">
                <Target className="w-5 h-5 text-indigo-400" /> Opcje Targetowania
              </h3>
              <p className="text-sm text-slate-400">Skonfiguruj wiek, lokalizację i szczegółowe zainteresowania grupy docelowej.</p>
            </div>
            <button onClick={() => setShowTargetingModal(true)}
              className="mt-4 md:mt-0 px-6 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-white font-bold rounded-xl transition-all flex items-center gap-2 whitespace-nowrap">
              Edytuj targetowanie <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {/* Targeting Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#0b0f19] p-5 rounded-2xl border border-slate-800">
              <div className="flex items-center gap-2 mb-3"><Users className="w-5 h-5 text-indigo-400" /><span className="text-sm font-bold text-slate-300">Wiek & Płeć</span></div>
              <p className="text-2xl font-black text-white">{targeting.age_min} – {targeting.age_max} lat</p>
              <p className="text-xs text-slate-500 mt-1">{targeting.genders?.[0] === 0 ? 'Wszyscy' : targeting.genders?.[0] === 1 ? 'Mężczyźni' : 'Kobiety'}</p>
            </div>
            <div className="bg-[#0b0f19] p-5 rounded-2xl border border-slate-800">
              <div className="flex items-center gap-2 mb-3"><Globe className="w-5 h-5 text-emerald-400" /><span className="text-sm font-bold text-slate-300">Lokalizacja</span></div>
              <p className="text-2xl font-black text-white">{targeting.geo_locations?.countries?.join(', ') || 'PL'}</p>
              <p className="text-xs text-slate-500 mt-1">{targeting.geo_locations?.cities?.length ? `+${targeting.geo_locations.cities.length} miast` : 'Cały kraj'}</p>
            </div>
            <div className="bg-[#0b0f19] p-5 rounded-2xl border border-slate-800">
              <div className="flex items-center gap-2 mb-3"><MapPin className="w-5 h-5 text-amber-400" /><span className="text-sm font-bold text-slate-300">Zainteresowania</span></div>
              <p className="text-2xl font-black text-white">{targeting.interests?.length || 0}</p>
              <p className="text-xs text-slate-500 mt-1">{targeting.interests?.slice(0,2).map((i: any) => i.name).join(', ') || 'Brak (szeroki zasięg)'}</p>
            </div>
          </div>
        </div>)}

        {/* ══════════════ STEP 3: Summary + Publish ══════════════ */}
        {wizardStep === 3 && (<div className="space-y-6 animate-in fade-in slide-in-from-right-4">
          <div className="bg-gradient-to-br from-indigo-500/5 to-blue-500/5 rounded-2xl border border-indigo-500/20 p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Eye className="w-5 h-5 text-indigo-400" /> Podsumowanie Kampanii</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Cel', value: objectiveLabels[campaign.objective] || campaign.objective, icon: Target, color: 'text-indigo-400' },
                { label: 'Budżet / dzień', value: `${campaign.daily_budget} PLN`, icon: DollarSign, color: 'text-emerald-400' },
                { label: 'Czas trwania', value: `${durationDays} dni`, icon: Calendar, color: 'text-blue-400' },
                { label: 'Koszt całkowity', value: `${totalBudget} PLN`, icon: DollarSign, color: 'text-amber-400' },
              ].map((s, i) => (
                <div key={i} className="bg-[#0b0f19] p-4 rounded-xl border border-slate-800">
                  <div className="flex items-center gap-2 mb-2"><s.icon className={`w-4 h-4 ${s.color}`} /><span className="text-[10px] font-bold text-slate-500 uppercase">{s.label}</span></div>
                  <p className="text-lg font-black text-white">{s.value}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-[#0b0f19] p-4 rounded-xl border border-slate-800">
                <p className="text-xs font-bold text-slate-500 uppercase mb-1">Nazwa</p>
                <p className="text-white font-bold">{campaign.name}</p>
              </div>
              <div className="bg-[#0b0f19] p-4 rounded-xl border border-slate-800">
                <p className="text-xs font-bold text-slate-500 uppercase mb-1">CTA</p>
                <p className="text-white font-bold">{ctaLabels[campaign.call_to_action] || campaign.call_to_action}</p>
              </div>
            </div>
            <div className="bg-[#0b0f19] p-4 rounded-xl border border-slate-800 mb-4">
              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Kreacje ({creatives.length})</p>
              <div className="flex gap-3 overflow-x-auto pb-1">
                {creatives.map((c, i) => (
                  <div key={c.id} className="shrink-0 w-28">
                    <div className="h-20 rounded-lg bg-slate-800 border border-slate-700 overflow-hidden mb-1.5 flex items-center justify-center">
                      {c.media_url ? (
                        c.media_type === 'video' ? <Film className="w-6 h-6 text-slate-500" /> :
                        <img src={c.media_url} className="w-full h-full object-cover" />
                      ) : <ImageIcon className="w-6 h-6 text-slate-600" />}
                    </div>
                    <p className="text-[10px] text-slate-400 truncate">{c.headline || `Kreacja #${i+1}`}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#0b0f19] p-4 rounded-xl border border-slate-800">
              <p className="text-xs font-bold text-slate-500 uppercase mb-1">Targetowanie</p>
              <p className="text-sm text-slate-300">{targeting.age_min}–{targeting.age_max} lat · {targeting.genders?.[0] === 0 ? 'Wszyscy' : targeting.genders?.[0] === 1 ? 'M' : 'K'} · {targeting.geo_locations?.countries?.join(', ') || 'PL'}{targeting.interests?.length ? ` · ${targeting.interests.length} zainteresowań` : ''}</p>
            </div>
          </div>
          <button onClick={publishCampaign} disabled={publishing}
            className="w-full px-10 py-5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:from-slate-700 disabled:to-slate-700 text-white rounded-xl font-black text-lg transition-all shadow-[0_0_30px_rgba(79,70,229,0.4)] flex items-center justify-center gap-3 group">
            {publishing ? (<><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Publikowanie w Meta Ads...</>) : (<>🚀 Opublikuj Kampanię w Meta Ads<ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></>)}
          </button>
        </div>)}

        {/* ══════════════ STEP NAVIGATION ══════════════ */}
        {wizardStep < 3 && (
          <div className="flex items-center justify-between pt-4 border-t border-slate-800/50">
            <button onClick={goBack} disabled={wizardStep === 0}
              className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700">
              <ChevronLeft className="w-4 h-4" /> Wstecz
            </button>
            <span className="text-xs text-slate-500 font-bold">Krok {wizardStep + 1} z 4</span>
            <button onClick={goNext}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/20">
              Dalej <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
        {wizardStep === 3 && (
          <div className="flex items-center justify-start pt-4 border-t border-slate-800/50">
            <button onClick={goBack}
              className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold transition-all bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700">
              <ChevronLeft className="w-4 h-4" /> Wróć do edycji
            </button>
          </div>
        )}
      </div>

      {showTargetingModal && (
        <AdvancedTargetingModal
          phase={1}
          initialTargeting={targeting}
          onClose={() => setShowTargetingModal(false)}
          onSave={setTargeting}
        />
      )}
    </div>
  );
}
